import { SEARCH_FOR_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useDebounce } from '@/hooks/useDebounce'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { cn } from '@/utils/cn'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { ChangeEvent, InputHTMLAttributes } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Input } from '../common/input'
import { SearchIcon } from '../icons/search'

interface Props {
  numberOfVaults: number
  trackingParams?: Record<string, string>
}

export const SearchFilter = ({
  numberOfVaults,
  trackingParams,
  className,
  ...props
}: Props & InputHTMLAttributes<HTMLInputElement>) => {
  const { searchFor, updateSearchState } = useVaultsSearchState()
  const [text, setText] = useState(searchFor ?? '')
  const { trackEvent } = useMixpanel()

  useEffect(() => {
    setText(searchFor ?? '')
  }, [searchFor])

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value)
      updateSearchState([SEARCH_FOR_KEY, event.target.value])
    },
    [updateSearchState]
  )

  useDebounce(
    () => {
      if (text !== '') {
        trackEvent('Filter used', { Filter: 'Search', Value: text, ...trackingParams })
      }
    },
    [text],
    1 * SECOND_IN_MILLISECONDS
  )

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <SearchIcon size={20} className="pointer-events-none absolute left-3 select-none text-body-foreground" />
      <Input
        placeholder={`Search through ${numberOfVaults} vaults`}
        value={text}
        onChange={onChange}
        inputMode="text"
        className="pl-10"
        {...props}
      />
    </div>
  )
}
